"use client";

import { useCallback } from "react";
import { useCookies } from "react-cookie";
import { usePostHog } from "posthog-js/react";
import { useDatadog } from "@auth/client-sdk-react";
import { ICheckoutV2SetupIntentData, KatKinProduct } from "@forms/schema";
import { useCatsStore, useCustomerStore, useDiscountStore } from "@./state";
import {
  getFlowFieldValuesForAction,
  SubmissionFieldValues,
} from "libs/state/src/lib/stores/useFormServiceStore";

import { useAPI } from "apps/website/hooks/useAPI";
import { useFlow } from "apps/website/contexts/flow";
import {
  ACTION_TRACKING_COOKIE_NAMES,
  getActionTrackingInfo,
} from "apps/website/utils/tracking/getActionTrackingInfo";

import { SYSTEM_ERROR_MESSAGES } from "../constants";
import { CreateBasketResult } from "../types";

const buildFieldSubmitValuesMap = (
  flowSlug: string,
  customerId: string,
  discountCode: string | undefined,
  catIds: string[],
): SubmissionFieldValues => {
  const fieldSubmitValuesMap = getFlowFieldValuesForAction(flowSlug);
  fieldSubmitValuesMap.set("customerId", { value: customerId, linkingId: "0" });
  if (discountCode) {
    fieldSubmitValuesMap.set("discountCode", { value: discountCode, linkingId: "0" });
  }
  fieldSubmitValuesMap.set("catIds", { value: catIds });
  return fieldSubmitValuesMap;
};

// V1 mirrors the legacy card flow: only `FRESH_TRIAL` uses the fresh checkout
// action; everything else falls back to the litter action.
const resolveActionName = (product: KatKinProduct): string => (
  product === "FRESH_TRIAL"
    ? "checkoutFreshNewCustomerV2"
    : "checkoutLitterNewCustomerV2"
);

/** V1 (forms-actions) version of `useExpressCheckout`.
 *
 * Mirrors the `getStripeIntent` flow inside `<CardDetailsFormCheckoutV2>` so
 * we can fetch a Stripe `clientSecret` for the PayPal Express Checkout button
 * when it lives inside a V1 form-service flow.
 *
 * The forms-actions back-end builds the basket itself from the form-service
 * field values (sourced via `getFlowFieldValuesForAction(flowSlug)`), scoped
 * to the customer ID stored per `flowSlug` in `useCustomerStore`.
 *
 * `flowSlug` and `formUsed` are read from `useFlow()` so this hook is only
 * usable inside a `<FlowProvider>` tree (which is already the case when
 * rendered via the `paypal_express_checkout` form-service field). */
export function useCreateBasket() {
  const { FormActions: formsActionsAPI } = useAPI();
  const datadog = useDatadog();
  const posthog = usePostHog();
  const [ cookies ] = useCookies(ACTION_TRACKING_COOKIE_NAMES);

  const { flow } = useFlow();
  const flowSlug = flow.slug;
  const formUsed = flow.slug;

  const customerStore = useCustomerStore();
  const discountStore = useDiscountStore();
  const { cats } = useCatsStore();

  const createBasket = useCallback(async (): Promise<CreateBasketResult> => {
    const product = flow.mappedFlow.product;
    if (!product) {
      const message = SYSTEM_ERROR_MESSAGES.missingProductForFlow(flowSlug);
      datadog.logger.error(message, { eventName: "ExpressCheckoutV1MissingProduct", flowSlug });
      return { success: false, message };
    }

    const customerId = customerStore.customerIds
      .find((customer) => customer.flowId === flowSlug)?.customerId;
    if (!customerId) {
      const message = SYSTEM_ERROR_MESSAGES.missingCustomerIdForFlow(flowSlug);
      datadog.logger.error(message, {
        eventName: "ExpressCheckoutV1MissingCustomerId",
        flowSlug,
        customerIds: [ ...customerStore.customerIds ],
      });
      return { success: false, message };
    }

    const discountCode = discountStore.discountCodes
      .find((discount) => discount.flowId === flowSlug)?.discountCode;
    const catIds = cats.find((dc) => dc.flowId === flowSlug)?.catIds ?? [];
    const actionName = resolveActionName(product);

    datadog.logger.info(
      `[useExpressCheckoutForV1] createBasket - START - flowSlug: ${flowSlug}, customerId: ${customerId}, discountCode: ${discountCode}, actionName: ${actionName}`,
    );

    const result = await formsActionsAPI.performAction<ICheckoutV2SetupIntentData>(
      actionName,
      buildFieldSubmitValuesMap(flowSlug, customerId, discountCode, catIds),
      product,
      formUsed,
      undefined,
      posthog.featureFlags.getFlagVariants(),
      getActionTrackingInfo(cookies),
      datadog.logger,
    );

    if (!result.success) {
      const message = result.message ?? SYSTEM_ERROR_MESSAGES.FORMS_ACTIONS_UNSUCCESSFUL;
      datadog.logger.error(SYSTEM_ERROR_MESSAGES.v1ActionFailed(message), {
        eventName: "ExpressCheckoutV1ActionFailed",
        result,
      });
      return { success: false, message, failedOn: result.failedOn };
    }

    datadog.logger.info("[useExpressCheckoutForV1] action succeeded", {
      eventName: "ExpressCheckoutV1ActionSucceeded",
      result,
    });

    return { success: true, ...result.responseBody };
  }, [
    cookies,
    customerStore.customerIds,
    discountStore.discountCodes,
    cats,
    flow.mappedFlow.product,
    flowSlug,
    formUsed,
    formsActionsAPI,
    posthog,
    datadog,
  ]);

  return { createBasket };
}
